'use client';
import React, { useState, useEffect } from 'react';
import { MapPin, Calendar } from 'lucide-react';
import Link from 'next/link';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { useBrandLogos } from '@/hooks/useBrandLogos';
import { createClient } from '@/lib/supabase/client';

// Program dimensions: 5.75 x 8.75 inches → ratio 0.657
// At display width 192px → height = 192 / 0.657 ≈ 292px
// Header = 20% of 292 ≈ 58px | Cover = 80% ≈ 234px
const PROG_W = 192;
const PROG_H = Math.round(PROG_W / (5.75 / 8.75)); // ≈ 292
const HEADER_H = Math.round(PROG_H * 0.2); // ≈ 58
const COVER_H = PROG_H - HEADER_H; // ≈ 234

const nearbyShows = [
  {
    id: 'nearby-001',
    title: 'Mamma Mia!',
    slug: 'mamma-mia',
    school: 'Cedar Ridge High School',
    city: 'Round Rock, TX',
    distance: '12 mi',
    dates: 'Apr 4–6, 2026',
    status: 'Running Now',
    statusColor: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    accentColor: 'rgba(16,185,129,0.7)',
    headerAccent: '#10b981',
    coverBg: 'linear-gradient(160deg, #064e3b 0%, #065f46 50%, #047857 100%)',
  },
  {
    id: 'nearby-002',
    title: 'Annie',
    slug: 'annie',
    school: 'Austin Community Theatre',
    city: 'Austin, TX',
    distance: '3 mi',
    dates: 'Apr 11–13, 2026',
    status: 'Opening Soon',
    statusColor: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    accentColor: 'rgba(245,158,11,0.7)',
    headerAccent: '#f59e0b',
    coverBg: 'linear-gradient(160deg, #7c2d12 0%, #b45309 50%, #d97706 100%)',
  },
  {
    id: 'nearby-003',
    title: 'Footloose',
    slug: 'footloose',
    school: 'Pflugerville High School',
    city: 'Pflugerville, TX',
    distance: '18 mi',
    dates: 'Apr 18–20, 2026',
    status: 'Upcoming',
    statusColor: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    accentColor: 'rgba(59,130,246,0.7)',
    headerAccent: '#3b82f6',
    coverBg: 'linear-gradient(160deg, #1e3a5f 0%, #1d4ed8 50%, #2563eb 100%)',
  },
  {
    id: 'nearby-004',
    title: 'Beauty and the Beast',
    slug: 'beauty-and-the-beast',
    school: 'Dripping Springs HS',
    city: 'Dripping Springs, TX',
    distance: '28 mi',
    dates: 'May 2–4, 2026',
    status: 'Upcoming',
    statusColor: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
    accentColor: 'rgba(139,92,246,0.7)',
    headerAccent: '#8b5cf6',
    coverBg: 'linear-gradient(160deg, #2e1065 0%, #5b21b6 50%, #7c3aed 100%)',
  },
];

export default function NearbyShows() {
  const [zip, setZip] = useState('');
  const [located, setLocated] = useState(true);
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal({ threshold: 0.1 });
  const { ref: gridRef, isVisible: gridVisible } = useScrollReveal({ threshold: 0.05 });
  const { logoWordmarkUrl } = useBrandLogos();
  const [coverImages, setCoverImages] = useState<Record<string, string | null>>({});

  useEffect(() => {
    async function fetchCovers() {
      const supabase = createClient();
      const slugs = nearbyShows.map((s) => s.slug);
      const { data } = await supabase
        .from('musicals')
        .select('slug, cover_image_url')
        .in('slug', slugs);
      if (data) {
        const map: Record<string, string | null> = {};
        data.forEach((row: { slug: string; cover_image_url: string | null }) => {
          map[row.slug] = row.cover_image_url;
        });
        setCoverImages(map);
      }
    }
    fetchCovers();
  }, []);

  return (
    <section id="nearby" className="py-24" style={{ background: 'linear-gradient(180deg, rgba(22,25,43,0.5) 0%, rgba(26,29,46,0.8) 100%)', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        <div
          ref={headerRef}
          className={`flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10 ${headerVisible ? 'reveal-up delay-0' : 'reveal-hidden'}`}
        >
          <div>
            <div className="flex items-center gap-2 mb-2">
              <MapPin size={18} className="text-violet-400" />
              <span className="text-xs font-bold text-violet-400 tracking-widest uppercase">Near You</span>
            </div>
            <h2 className="text-3xl font-extrabold text-zinc-100">Performances Nearby</h2>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-4 py-2.5" style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px' }}>
              <MapPin size={14} className="text-zinc-500" />
              <input
                type="text"
                value={zip}
                onChange={(e) => setZip(e?.target?.value)}
                placeholder="Enter ZIP code"
                className="bg-transparent text-sm text-zinc-200 placeholder-zinc-500 outline-none w-32"
              />
            </div>
            <button
              onClick={() => setLocated(true)}
              className="text-white text-sm font-semibold px-4 py-2.5 transition-all duration-150 active:scale-95"
              style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)', borderRadius: '4px' }}
            >
              Update
            </button>
          </div>
        </div>

        {located && (
          <div ref={gridRef} className={`grid grid-cols-2 sm:grid-cols-4 gap-4 justify-items-center ${gridVisible ? 'reveal-fade delay-100' : 'reveal-hidden'}`}>
            {nearbyShows?.map((show, idx) => {
              const coverUrl = coverImages[show.slug];
              return (
                <Link
                  key={show?.id}
                  href="/program-viewer"
                  className={`group hover:scale-[1.05] hover:-translate-y-1.5 transition-all duration-250 ease-out hover:shadow-[0_16px_40px_rgba(0,0,0,0.4)] relative overflow-hidden ${gridVisible ? 'reveal-up' : 'reveal-hidden'}`}
                  style={{
                    width: PROG_W,
                    height: PROG_H,
                    border: `1px solid ${show?.accentColor}`,
                    borderRadius: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    animationDelay: gridVisible ? `${idx * 80 + 150}ms` : '0ms',
                  }}
                >
                  {/* Accent line on left edge */}
                  <div className="absolute top-0 left-0 w-0.5 h-full z-10 transition-all duration-250 group-hover:w-1" style={{ background: `linear-gradient(180deg, ${show?.headerAccent}, ${show?.accentColor})` }} />

                  {/* 20% black header */}
                  <div
                    className="flex flex-col items-center justify-center px-4 shrink-0"
                    style={{ height: HEADER_H, backgroundColor: '#09090b' }}
                  >
                    {logoWordmarkUrl ? (
                      <img
                        src={logoWordmarkUrl}
                        alt="Ovation"
                        className="object-contain"
                        style={{ height: '14px', filter: 'brightness(0) saturate(100%) invert(1) sepia(1) saturate(2) hue-rotate(0deg)', maxWidth: '80%' }}
                      />
                    ) : (
                      <span className="font-bold tracking-[0.15em] uppercase" style={{ fontSize: 8, color: show?.headerAccent }}>Program</span>
                    )}
                    <div className="text-white/60 leading-tight text-center mt-0.5" style={{ fontSize: 6 }}>{show?.school}</div>
                    <div className="text-white/40 leading-tight text-center" style={{ fontSize: 6 }}>{show?.city}</div>
                  </div>

                  {/* 80% cover area */}
                  {coverUrl ? (
                    <div className="relative overflow-hidden" style={{ height: COVER_H }}>
                      <img
                        src={coverUrl}
                        alt={`${show.title} cover`}
                        className="w-full h-full object-cover"
                      />
                      {/* Status badge overlay */}
                      <div className="absolute top-2 left-2 right-2 flex justify-between items-start">
                        <span
                          className={`text-[9px] font-bold px-1.5 py-0.5 border ${show?.statusColor}`}
                          style={{ borderRadius: 2 }}
                        >
                          {show?.status}
                        </span>
                        <span className="text-[9px] text-white/80 font-medium bg-black/40 px-1 rounded">{show?.distance}</span>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="relative flex flex-col items-center justify-between p-3"
                      style={{ height: COVER_H, background: show?.coverBg }}
                    >
                      {/* Status badge */}
                      <div className="w-full flex justify-between items-start">
                        <span
                          className={`text-[9px] font-bold px-1.5 py-0.5 border ${show?.statusColor}`}
                          style={{ borderRadius: 2 }}
                        >
                          {show?.status}
                        </span>
                        <span className="text-[9px] text-white/60 font-medium">{show?.distance}</span>
                      </div>

                      {/* Title block */}
                      <div className="flex-1 flex flex-col items-center justify-center text-center px-1">
                        <div className="text-[8px] text-white/50 tracking-widest uppercase font-sans mb-1">Musical Program</div>
                        <div className="text-[11px] font-bold text-white font-sans leading-tight">{show?.title}</div>
                        <div className="mt-2 w-8 h-px" style={{ backgroundColor: show?.headerAccent }} />
                      </div>

                      {/* School / dates */}
                      <div className="w-full">
                        <div className="flex items-center gap-1 text-[8px] text-white/50">
                          <Calendar size={9} />
                          <span>{show?.dates}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}